/**
 * AccountPlan — shared truth per account, audit-trail tracked
 *
 * One plan per account. Multiple opportunities may spawn inside a single account.
 * bootcamp_seed fields are pre-populated from the Top 10 worksheet (read-mostly).
 * mpact fields are filled by ae/bdr as discovery progresses.
 * All writes append an AuditEntry so the history is never lost.
 *
 * Roles: bdr, ae, manager, executive, marketing, se — NEVER "rep"
 * M-PACT always spelled with hyphen
 * Brand: Navy #0B0B0F, Red #DC2626, AIP bg #FAFAF7
 */

// ---------------------------------------------------------------------------
// Supporting sub-types
// ---------------------------------------------------------------------------

/**
 * A person in the account's relationship map.
 * owner reflects the M-PACT role category this person plays in the deal.
 * warmth is a 1–5 score: 1 = cold / unknown, 5 = strong personal relationship.
 */
export interface RelationshipNode {
  name: string;
  role: string;                        // e.g. "CTO", "Procurement Director"
  org: string;                         // company or division
  owner:
    | "champion"
    | "economic_buyer"
    | "user_buyer"
    | "technical_buyer"
    | "fox"                            // informal power broker
    | "detractor"
    | "blocker"
    | "coach"
    | "unknown";
  warmth: 1 | 2 | 3 | 4 | 5;
  last_touch: string;                  // ISO 8601 date string or empty ""
  notes: string;
}

/**
 * A single opportunity inside an account.
 * One account can have multiple opportunities (e.g. Plant 1 pilot, Plant 2 expansion).
 * stage maps to the M-PACT checkpoint the deal is currently clearing.
 */
export interface Opportunity {
  id: string;                          // uuid
  name: string;                        // e.g. "Caldera Industrial — Plant 1 Profit+ Pilot"
  stage: OpportunityStage;
  expected_close: string;              // ISO 8601 date
  amount: number;                      // USD
  win_probability: number;             // 0–100 integer
  forecast_bucket: ForecastBucket;
  next_step: string;
  next_step_date: string;              // ISO 8601 date — must always be a real booked date
  competition: string;
  decision_criteria: string[];
  blockers: string[];
  composite_score: number;             // 0–100, computed server-side from gate answers
  created_by: string;                  // user id of ae/bdr who opened it
  created_at: string;                  // ISO 8601
  updated_at: string;                  // ISO 8601
}

/**
 * M-PACT stage names aligned to the 10 locked checkpoints (CP1–CP10).
 * Matches the locked CP sequence in MI6_MASTER_DECISIONS_LOCKED.md.
 */
export type OpportunityStage =
  | "cp1_project"          // Is there a real project?
  | "cp2_pain"             // Pain named and quantified
  | "cp3_demonstration"    // Pain checkpoint (legacy key: cp3_demonstration)
  | "cp4_budget"           // Budget real and sourced
  | "cp5_timeline"         // Decision + implementation windows confirmed
  | "cp6_authority"        // Economic/technical/legal signers named
  | "cp7_people"           // Champion, supporters, detractors mapped
  | "cp8_competition"      // Competitive position confirmed
  | "cp9_finance"          // Payment terms, capex/opex, approval path
  | "cp10_legal"           // MSA, redlines, paper path open
  | "closed_won"
  | "closed_lost"
  | "disqualified";

/**
 * Forecast bucket — canon §6 (2026-06-09). 5 score-driven buckets.
 * The score badge carries color; the bucket label is text-only.
 * Commit:    composite >= 80   color #16A34A
 * Upside:    composite 65-79   color #16A34A
 * Best Case: composite 50-64   color #EAB308
 * Pipeline:  composite 30-49   color #EA580C
 * Omit:      composite < 30    color #DC2626
 * Won is a deal STATUS not a forecast bucket.
 * No Probable. No weighted percentages.
 */
export type ForecastBucket =
  | "Commit"
  | "Upside"
  | "Best Case"
  | "Pipeline"
  | "Omit";

/**
 * A single M-PACT checkpoint answer field.
 * Every CP field follows this shape so the audit trail is consistent.
 * evidence_state maps to the Rules Matrix states (Evidence / Assumption / Unknown).
 */
export interface CheckpointField {
  answered: boolean;
  answer: string;
  evidence_state: "evidence" | "assumption" | "unknown" | "";
  gate_flag: "OK" | "AMBER" | "SOFT_STOP" | "HARD_STOP" | "";
  points_earned: number;              // 0 if unanswered
  updated_by: string;                 // user id
  updated_at: string;                 // ISO 8601
  notes: string;
}

/**
 * A single entry in the account's immutable audit log.
 * Never deleted — appended only.
 */
export interface AuditEntry {
  id: string;                         // uuid
  field: string;                      // dot-path, e.g. "mpact.cp1_project.answer"
  old_value: string;
  new_value: string;
  changed_by: string;                 // user id
  changed_at: string;                 // ISO 8601
  session_note: string;               // optional context for the change
}

/**
 * Next best play for the daily AE view.
 * Auto-ranked by the ranking algorithm (see top10-plan-schema-2026-05-07.md).
 * AE can override the suggested type and date.
 */
export interface NextBestPlay {
  play_type: "close" | "advance" | "discover" | "disqualify";
  description: string;
  revenue_impact_estimate: number;    // USD — used by ranking algorithm
  suggested_owner: string;            // user id of ae or bdr
  suggested_date: string;             // ISO 8601
  auto_ranked: boolean;               // true = system-generated; false = ae override
  rank_score: number;                 // composite rank score (see ranking algorithm)
}

/**
 * Disqualification thresholds for this account.
 * When composite_score falls below threshold_score OR the account goes stale,
 * the system recommends DISQUALIFY and prompts the ae to confirm.
 * Defaults: threshold_score = 40, days_without_progress = 14 (soft-kill) / 30 (hard-kill)
 */
export interface DisqualificationCriteria {
  threshold_score: number;            // composite below this = recommend drop
  days_without_progress_soft: number; // default 14 — soft-kill warning
  days_without_progress_hard: number; // default 30 — hard-kill, auto-omit from forecast
  unresolved_blockers: string[];
  disqualified: boolean;
  disqualified_at: string;            // ISO 8601 or ""
  disqualified_by: string;            // user id or ""
  disqualification_reason: string;
}

// ---------------------------------------------------------------------------
// M-PACT checkpoint block
// One field per CP1–CP10. Each maps to a CheckpointField.
// ---------------------------------------------------------------------------

export interface MpactBlock {
  /** CP1 — Project: Is there a real project? New logo or expansion? */
  cp1_project: CheckpointField;

  /** CP2 — Pain: Pain named, quantified, in the prospect's own words. */
  cp2_pain: CheckpointField;

  /** CP3 — Pain: pain confirmed in prospect's own words, quantified, corroborated. */
  cp3_demonstration: CheckpointField;

  /** CP4 — Budget: Real budget, named source, validated line item confirmed by EB. */
  cp4_budget: CheckpointField;

  /** CP5 — Timeline: Decision window AND implementation window both confirmed as real. */
  cp5_timeline: CheckpointField;

  /** CP6 — Authority: Who actually signs — economic, technical, security, legal named. */
  cp6_authority: CheckpointField;

  /** CP7 — People: Champion, supporters, influencers, detractors mapped.
   *  Political, personal, professional agendas understood. */
  cp7_people: CheckpointField;

  /** CP8 — Competition: Where Mi6/Attilius stands vs alternatives in the prospect's head,
   *  evaluated not assumed. */
  cp8_competition: CheckpointField;

  /** CP9 — Finance: Payment terms, capex/opex, approval thresholds, sourcing process. */
  cp9_finance: CheckpointField;

  /** CP10 — Legal: MSA, redlines, contract terms, paper path open. */
  cp10_legal: CheckpointField;

  /** Computed composite score 0–100, derived server-side from all CP answers
   *  using the Rules Matrix point weights. */
  composite_score: number;

  /** Highest flag across all checkpoints — determines forecast posture cap. */
  highest_flag: "OK" | "AMBER" | "SOFT_STOP" | "HARD_STOP" | "NONE";

  /** Derived forecast posture from score + flags. */
  forecast_posture: "COMMIT" | "STRETCH" | "WORST_CASE" | "DISQUALIFY" | "UNSCORED";
}

// ---------------------------------------------------------------------------
// Bootcamp seed block — read-mostly, carried forward from Top 10 worksheet
// ---------------------------------------------------------------------------

export interface BootcampSeed {
  /** Why Mi6 picked this account for the Top 10. */
  why_this_account: string;

  /** Initial pain hypothesis before any discovery call. */
  initial_pain_hypothesis: string;

  /** Existing relationships the ae/bdr can activate immediately. */
  relationship_map: RelationshipNode[];

  /** How the account came to the team's awareness (referral, event, research, warm intro). */
  source_of_awareness: string;

  /** Known or suspected competitive situation at account entry. */
  competitive_intel: string;

  /** Prior contact history, dormant deals, or prior attempts to engage. */
  historical_attempts: string;

  /** The desired first move as agreed at bootcamp. */
  first_move: string;

  /** Who Mi6 wants to reach inside this account. */
  target_personas: string;
}

// ---------------------------------------------------------------------------
// Root AccountPlan
// ---------------------------------------------------------------------------

export interface AccountPlan {
  /** Matches account.id in Supabase. */
  id: string;
  account_id: string;
  account_name: string;

  /** Manufacturing vertical sub-segment (e.g. "OEM automotive", "Tier-1 supplier",
   *  "Industrial tech", "EV / new entrant"). */
  industry: string;

  /** ICP fit confirmed per Gate 0 of the Rules Matrix. */
  icp_confirmed: boolean;

  /** Supabase tenant id. Tenant 1 = Mi6 Group. */
  tenant_id: number;

  /** ae user id who owns this account plan. */
  owner_id: string;

  /** bdr user id currently working the top-of-funnel for this account. */
  bdr_id: string;

  // -------------------------------------------------------------------------
  // Bootcamp-prefilled section (read-mostly)
  // -------------------------------------------------------------------------
  bootcamp_seed: BootcampSeed;

  // -------------------------------------------------------------------------
  // M-PACT qualification block (ae/bdr fills as discovery progresses)
  // -------------------------------------------------------------------------
  mpact: MpactBlock;

  // -------------------------------------------------------------------------
  // Opportunities spawned within this account
  // -------------------------------------------------------------------------
  opportunities: Opportunity[];

  // -------------------------------------------------------------------------
  // Daily play recommendation — auto-ranked, ae can override
  // -------------------------------------------------------------------------
  next_best_play: NextBestPlay;

  // -------------------------------------------------------------------------
  // Disqualification guardrails (move on fast rule)
  // -------------------------------------------------------------------------
  disqualification: DisqualificationCriteria;

  // -------------------------------------------------------------------------
  // Immutable audit trail — never deleted, append-only
  // -------------------------------------------------------------------------
  audit_log: AuditEntry[];

  // -------------------------------------------------------------------------
  // Metadata
  // -------------------------------------------------------------------------
  created_at: string;                  // ISO 8601
  updated_at: string;                  // ISO 8601
  last_activity_at: string;            // ISO 8601, or empty string "" when no activity has ever been logged. Drives stale detection.
  rank_in_top10: number;               // 1–10, maintained by manager
}

// ---------------------------------------------------------------------------
// Blank checkpoint field factory — used to initialize unanswered CPs
// ---------------------------------------------------------------------------
export const blankCheckpoint = (): CheckpointField => ({
  answered: false,
  answer: "",
  evidence_state: "",
  gate_flag: "",
  points_earned: 0,
  updated_by: "",
  updated_at: "",
  notes: "",
});

// ---------------------------------------------------------------------------
// Blank M-PACT block factory
// ---------------------------------------------------------------------------
export const blankMpact = (): MpactBlock => ({
  cp1_project: blankCheckpoint(),
  cp2_pain: blankCheckpoint(),
  cp3_demonstration: blankCheckpoint(),
  cp4_budget: blankCheckpoint(),
  cp5_timeline: blankCheckpoint(),
  cp6_authority: blankCheckpoint(),
  cp7_people: blankCheckpoint(),
  cp8_competition: blankCheckpoint(),
  cp9_finance: blankCheckpoint(),
  cp10_legal: blankCheckpoint(),
  composite_score: 0,
  highest_flag: "NONE",
  forecast_posture: "UNSCORED",
});

// ---------------------------------------------------------------------------
// Default disqualification thresholds (locked: soft 14d, hard 30d)
// ---------------------------------------------------------------------------
export const defaultDisqualification = (): DisqualificationCriteria => ({
  threshold_score: 40,
  days_without_progress_soft: 14,
  days_without_progress_hard: 30,
  unresolved_blockers: [],
  disqualified: false,
  disqualified_at: "",
  disqualified_by: "",
  disqualification_reason: "",
});
